import { EntityAttributes, EntityOptions } from "./entity-types";
import { useEntityDetail } from "../../../../../api/BaseEntityApi";
import { IGenericEntity, INamedEntity } from "../../../../../api/GenericTypes";
import { EntityWrapper, GetPosition } from "./EntityWrapper/EntityWrapper";
import { LoadingWrapper } from "../../../../../common/LoadingWrapper";
import { Alert } from "../../../../../common/overlays/Alert/Alert";
import { toUppercase } from "../../../../../common/helperfunctions/stringFunctions";
import { InventoryItemsDetailTable } from "../../../../../Inventories/InventoryItemsDetail";
import { InventoryItem, InventoryItemFieldLabels, inventoryItemsConstants } from "../../../../../api/Inventories";
import { PersonDetailTable } from "../../../../../Persons/PersonsDetail";
import { Person, PersonFieldLabels, personsConstants } from "../../../../../api/Person";
import { ProjectsDetailTable } from "../../../../../Projects/ProjectsDetail";
import { Project, ProjectFieldLabels, projectsConstants } from "../../../../../api/Projects";
import { SampleDetailTable } from "../../../../../samples/SamplesDetail";
import { Sample, SampleFieldLabels, samplesConstants } from "../../../../../api/Samples";
import { Dataset, DatasetFilters } from "../../../../../api/Datasets";
import { DatasetControls } from "./entities/datasets/DatasetControls";
import { DatasetWrapper } from "./entities/datasets/DatasetWrapper";
import { isEqual, uniqueId } from "remirror";
import { genericEntityConstants } from "../../../../../api/GenericConstants";
import { LabNotebookExperimentDetailContentTable } from "../../../../LabNotebookExperiments/LabNotebookExperimentDetailContentTable";
import {
  LabNotebookExperiment,
  labNotebookExperimentFieldLabels,
  labNotebookExperimentsConstants,
} from "../../../../types/LabNotebookExperiment";
import { LabNotebook, labNotebookFieldLabels, labNoteBooksConstants } from "../../../../types/LabNotebook";
import { LabNotebookEntryDetailContentTable } from "../../../../LabNotebookEntries/LabNotebookEntryDetailContentTable";
import {
  labNotebookEntriesConstants,
  LabNotebookEntry,
  labNotebookEntryFieldLabels,
} from "../../../../types/LabNotebookEntry";
import { LabNotebooksDetailTable } from "../../../../LabNotebooks/LabNotebookDetailView";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";

const getEntityParams = (entityTypeId?: string) => {
  if (!entityTypeId) return undefined;
  switch (entityTypeId) {
    case "datasets":
    case "attachments":
      return {
        includeCount: true,
        includeParsedMetadata: true,
        includeParsingState: true,
      } as DatasetFilters;
    default:
      return undefined;
  }
};

export const EntityViews = ({
  attrs,
  getPosition,
  updateAttributes,
  options,
}: {
  attrs: EntityAttributes;
  getPosition: GetPosition;
  updateAttributes: (attrs: EntityAttributes) => void;
  options?: EntityOptions;
}) => {
  const entityConstants =
    !!attrs?.entityTypeId && Object.hasOwn(genericEntityConstants, attrs.entityTypeId)
      ? genericEntityConstants[attrs?.entityTypeId].entityConstants
      : undefined;

  const {
    data: entity,
    status,
    fetchStatus,
    error,
  } = useEntityDetail<IGenericEntity & INamedEntity>(
    entityConstants?.resource!,
    attrs?.entityId!,
    getEntityParams(attrs?.entityTypeId!),
    {
      enabled: !!entityConstants?.resource && !!attrs?.entityId,
    }
  );

  const [subContentIsLoading, setSubContentIsLoading] = useState<boolean>(
    attrs.entityTypeId === "datasets" || attrs.entityTypeId === "attachments" // datasets and attachments have sub-content that needs to be loaded
  );
  const fallbackId = useMemo(() => uniqueId(), []);
  useEffect(() => {
    options?.handleBlockLoadingStateChange?.(
      attrs.id ?? fallbackId,
      status === "loading" || fetchStatus === "fetching" || subContentIsLoading
    );
  }, [options, attrs.id, fallbackId, status, fetchStatus, subContentIsLoading]);

  return (
    <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error} type="skeleton-rows">
      {entity ? (
        <EntityWrapper
          attrs={attrs}
          entity={entity}
          getPosition={getPosition}
          updateAttributes={updateAttributes}
          additionalSettingsControls={EntityControlSwitch({
            entity,
            attrs,
            getPosition,
            updateAttributes,
            options,
          })}
          options={options}
        >
          {EntityComponentSwitch({
            entity,
            attrs,
            getPosition,
            updateAttributes,
            options,
            setSubContentIsLoading,
          })}
        </EntityWrapper>
      ) : (
        <Alert
          fit
          centered
          type="warning"
          message={`${toUppercase(entityConstants?.entitySingular ?? "item")} with ID ${
            attrs.entityId ?? "-"
          } could not be found`}
          style={{ marginBottom: "15px" }}
        />
      )}
    </LoadingWrapper>
  );
};

const EntityComponentSwitch = ({
  entity,
  attrs,
  getPosition,
  updateAttributes,
  options,
  setSubContentIsLoading,
}: {
  entity: IGenericEntity & INamedEntity;
  attrs: EntityAttributes;
  getPosition: GetPosition;
  updateAttributes: (attrs: EntityAttributes) => void;
  options?: EntityOptions;
  setSubContentIsLoading: Dispatch<SetStateAction<boolean>>;
}) => {
  switch (attrs?.entityTypeId) {
    case "datasets":
    case "attachments":
      return (
        <DatasetWrapper
          entity={entity as Dataset}
          attrs={attrs}
          getPosition={getPosition}
          updateAttributes={updateAttributes}
          onDoubleClick={options?.onDoubleClick}
          ignoreIntersectionObserver={options?.ignoreIntersectionObserver}
          setSubContentIsLoading={setSubContentIsLoading}
        />
      );
    case "inventoryItems":
      return (
        <InventoryItemsDetailTable
          entityTypeId="inventoryItems"
          entity={entity as InventoryItem}
          entityConstants={inventoryItemsConstants}
          fieldLabels={InventoryItemFieldLabels}
          showHierarchy
        />
      );
    case "persons":
      return (
        <PersonDetailTable
          entityTypeId="persons"
          entity={entity as Person}
          entityConstants={personsConstants}
          fieldLabels={PersonFieldLabels}
        />
      );
    case "projects":
      return (
        <ProjectsDetailTable
          entityTypeId="projects"
          entity={entity as Project}
          entityConstants={projectsConstants}
          fieldLabels={ProjectFieldLabels}
        />
      );
    case "samples":
      return (
        <SampleDetailTable
          entityTypeId="samples"
          entity={entity as Sample}
          entityConstants={samplesConstants}
          fieldLabels={SampleFieldLabels}
        />
      );
    case "notebookExperiments":
      return (
        <LabNotebookExperimentDetailContentTable
          entityTypeId="notebookExperiments"
          entity={entity as LabNotebookExperiment}
          entityConstants={labNotebookExperimentsConstants}
          fieldLabels={labNotebookExperimentFieldLabels}
        />
      );
    case "notebooks":
      return (
        <LabNotebooksDetailTable
          entityTypeId="notebooks"
          entity={entity as LabNotebook}
          entityConstants={labNoteBooksConstants}
          fieldLabels={labNotebookFieldLabels}
        />
      );
    case "notebookEntries":
      return (
        <LabNotebookEntryDetailContentTable
          entityTypeId="notebookEntries"
          entity={entity as LabNotebookEntry}
          entityConstants={labNotebookEntriesConstants}
          fieldLabels={labNotebookEntryFieldLabels}
        />
      );

    default:
      return (
        <Alert
          fit
          centered
          type="warning"
          message="No view available for this item."
          style={{ marginBottom: "15px" }}
        />
      );
  }
};

const EntityControlSwitch = ({
  entity,
  attrs,
  getPosition,
  updateAttributes,
  options,
}: {
  entity: IGenericEntity & INamedEntity;
  attrs: EntityAttributes;
  getPosition: GetPosition;
  updateAttributes: (attrs: EntityAttributes) => void;
  options?: EntityOptions;
}) => {
  switch (attrs.entityTypeId) {
    case "datasets":
    case "attachments":
      return (
        <DatasetControls
          entity={entity as Dataset}
          attrs={attrs}
          getPosition={getPosition}
          updateAttributes={updateAttributes}
        />
      );

    default:
      return null;
  }
};

export const compareAndUpdateAttributes = (
  newAttrs: EntityAttributes,
  lastAttrs: EntityAttributes,
  updateFunction: (attrs: EntityAttributes) => void
) => {
  if (!isEqual(newAttrs, lastAttrs)) {
    updateFunction(newAttrs);
  }
};
